<template>
  <Page class="page" :filters="filters">
    <div class="title">{{ $t('report.exportIntroduction') }}</div>
    <div class="subtitle">{{ $t('report.studyParameters') }}</div>

    <div class="content">
      <div v-if="computedFilters.length > 0" class="filter-line">{{ $t('report.filterLine') }}</div>

      <div class="filter-container">
        <div v-for="filterType in computedFilters" :key="filterType.id" class="filter-type-container">
          <div class="filter-title">{{ filterType.title }} :</div>
          <div v-for="filter in filterType.values" :key="filter" class="filter">{{ $t(filter) }}</div>
        </div>
      </div>

      <div class="data-container">
        <div class="item">
          <div class="icon-container">
            <i class="icon icon-calendar"></i>
          </div>
          <div class="item-title">{{ $t('studyDate') }}</div>
          <div class="value">{{ date }}</div>
        </div>
        <div class="divider"></div>
        <div class="item">
          <div class="icon-container">
            <i class="icon icon-clock"></i>
          </div>
          <div class="item-title">{{ $t('studyTime') }}</div>
          <div class="value">{{ travelTimeType }} {{ time }}</div>
        </div>
        <div class="divider"></div>
        <div class="item">
          <div class="icon-container">
            <i class="icon icon-directions"></i>
          </div>
          <div class="item-title">{{ $t('routeType') }}</div>
          <div class="value">{{ travelType }}</div>
        </div>
        <div class="divider"></div>
        <div class="item">
          <div class="icon-container">
            <i class="icon icon-bus"></i>
          </div>
          <div class="item-title">{{ $t('calculationBasis') }}</div>
          <div class="value">
            <span v-for="(text, i) in base" :key="i">{{ text }}{{ i !== base.length - 1 ? ', ' : null }}</span>
          </div>
        </div>
      </div>
      <div v-if="study" class="info-container">
        <div class="item">
          <div class="value">{{ study.collaborators_total }}</div>
          <div class="name">{{ $t('parameters.totalCollaborators', study.collaborators_total) }}</div>
        </div>
        <div class="item">
          <div class="value">{{ filterCollaborators.length }}</div>
          <div class="name">{{ $t('parameters.accountedCollaborators', filterCollaborators.length) }}</div>
        </div>
        <div class="item">
          <div class="value">{{ study.unrecognized_addresses }}</div>
          <div class="name">{{ $t('parameters.unrecognizedAddress', study.unrecognized_addresses) }}</div>
        </div>
        <div class="item">
          <div class="value">{{ study.off_limit_addresses }}</div>
          <div class="name">{{ $t('parameters.offlimitAddress', study.off_limit_addresses) }}</div>
        </div>
        <div class="item">
          <div class="value">{{ study.excluded_addresses }}</div>
          <div class="name">{{ $t('parameters.excludedAddress', study.excluded_addresses) }}</div>
        </div>
      </div>
    </div>
  </Page>
</template>

<script>
import Page from '@/components/Report/BasePage';

export default {
  name: 'IntroductionConfigPage',

  components: {
    Page,
  },

  props: {
    study: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    export: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    disableCount: {
      type: [Boolean, Number],
      required: false,
      default: false,
    },

    filters: {
      type: String,
      required: false,
      default: null,
    },

    filterCollaborators: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  computed: {
    computedFilters() {
      const filters = [
        {
          id: 1,
          title: this.$t('filters.contract_types'),
          values: this.mapFilter('contract_types'),
        },
        {
          id: 2,
          title: this.$t('filters.favorite_transports'),
          values: this.mapFilter('favorite_transports'),
        },
        {
          id: 3,
          title: this.$t('filters.services'),
          values: this.mapFilter('services'),
        },
        {
          id: 4,
          title: this.$t('filters.seniorities'),
          values: this.mapFilter('seniorities'),
        },
        {
          id: 5,
          title: this.export.filters.filter1_name,
          values: this.mapFilter('filter1'),
        },
        {
          id: 6,
          title: this.export.filters.filter2_name,
          values: this.mapFilter('filter2'),
        },
      ];

      return filters.filter(e => e.values.length).sort((a, b) => a.values.length - b.values.length);
    },

    date() {
      if (!this.study) return;

      if (typeof this.study.date === 'number') {
        // legacy
        return this.$d(new Date(this.study.date * 1000), 'date');
      }

      return this.$d(new Date(this.study.date), 'date');
    },

    travelTimeType() {
      if (!this.study) {
        return;
      }
      return this.study.travel_time_type === 1 ? `${this.$t('arrivalAt')} ` : `${this.$t('departureAt')} `;
    },

    time() {
      if (!this.study) return;

      let dateTime = new Date(this.study.date);
      if (typeof this.study.date === 'number') {
        // legacy
        dateTime = new Date(this.study.date * 1000);
      }

      return `${dateTime.getHours()}h${dateTime.getMinutes() > 0 ? dateTime.getMinutes() : ''}`;
    },

    travelType() {
      if (!this.study) {
        return;
      }
      return this.study.travel_type === 1 ? this.$t('routeOneWay') : this.$t('routeReturn');
    },

    base() {
      if (!this.study) {
        return;
      }
      const base = [];
      if (this.study.individual_transport_only === false) {
        base.push(this.$t('transportMode.public') + (this.study.futur_transport_type !== 0 ? ` ${this.$t('transportMode.actuals')}` : ''));

        if (this.study.futur_transport_type !== 0) {
          const futureTransportTypeMap = {
            1: 'transportMode.reach2025',
            2: 'transportMode.reach2030',
            3: 'transportMode.reach2027',
            4: 'transportMode.reach2031',
          };

          base.push(this.$t('transportMode.public') + ' ' + this.$t(futureTransportTypeMap[this.study.futur_transport_type]));
        }
      }
      base.push(this.$t('transportMode.private'));
      if (this.study.individual_transport_only === false) {
        base.push(this.$t('transportMode.bicycle'));

        if (this.study.withWalkingTimes) {
          base.push(this.$t('transportMode.walking'));
        }
      }
      return base;
    },
  },

  methods: {
    mapFilter(filtersName) {
      if (!filtersName || !this.export) {
        return [];
      }

      return this.export.filters[filtersName]
        .reduce((acc, val) => acc.concat(val), [])
        .filter(e => e && !e.enabled && e.value)
        .map(e => e.value);
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';

.page {
  padding: 48px 80px;
  box-sizing: border-box;

  .title {
    display: flex;
    flex-direction: column;
    font-size: 32px;
    font-weight: 600;
    color: #3c3c3b;
    margin-bottom: 16px;

    &::after {
      content: '';
      margin: 8px 0;
      width: 64px;
      height: 4px;
      border-radius: 4px;
      background: colors.$main-color;
    }
  }

  .subtitle {
    display: flex;
    flex-direction: column;
    font-size: 24px;
    font-weight: 200;
    color: #3c3c3b;
    margin-bottom: 16px;
  }

  .content {
    .filter-container {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      overflow: hidden;
      max-height: 112px;
      min-height: 76px;

      .filter-type-container {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        margin-right: 16px;
      }

      .filter-title {
        font-weight: bold;
        color: #3c3c3b;
        margin: 4px 12px 4px 0;
      }

      .filter {
        height: 24px;
        line-height: 24px;
        padding: 2px 8px;
        margin: 4px 8px 4px 0;
        min-width: 32px;
        text-align: center;
        border: solid 1px colors.$grey-color;
        border-radius: 24px;
      }
    }

    .data-container {
      display: flex;
      justify-content: space-around;
      align-items: center;
      width: 100%;
      height: 256px;
      margin: 24px 0;

      .item {
        width: 160px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        .icon-container {
          flex-shrink: 0;
          display: flex;
          justify-content: center;
          align-items: center;
          $radius: 64px;
          border: solid 2px colors.$dark-beige-color;
          width: $radius;
          height: $radius;
          border-radius: $radius;

          .icon {
            color: colors.$dark-beige-color;
            font-size: 48px;
          }
        }
        .item-title {
          margin: 16px 0 8px;
          font-size: 16px;
          font-weight: 600;
        }
        .value {
          height: 64px;
          text-align: center;
          color: colors.$dark-color;
        }
      }

      .divider {
        width: 1px;
        height: 128px;
        background: colors.$dark-beige-color;
      }
    }

    .info-container {
      width: 100%;
      height: 128px;
      padding: 16px 8px;
      background: colors.$beige-color;
      display: flex;
      justify-content: space-around;
      box-sizing: border-box;

      .item {
        width: 192px;
        font-size: 16px;
        text-align: center;
        color: colors.$black-color;

        .value {
          font-weight: 200;
          font-size: 48px;
        }
        .name {
          font-size: 16px;
        }
      }
    }
  }
}
</style>
